import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Plus,
  Search,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Printer,
  Pencil,
  Trash2,
  ClipboardList,
  Filter,
  Download,
  X,
  CheckCircle2,
  Clock,
  AlertTriangle,
  NotebookPen,
} from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import HoursByEmployeeChart from "@/components/HoursByEmployeeChart";
import ClienteCombobox from "@/components/ClienteCombobox";
import CommessaClienteChip from "@/components/CommessaClienteChip";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";

const formatDateIT = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("it-IT", {
    weekday: "long", day: "2-digit", month: "2-digit", year: "numeric",
  });
};

function startOfWeek(d) {
  const x = new Date(d);
  const day = x.getDay();
  const diff = (day === 0 ? -6 : 1 - day);
  x.setDate(x.getDate() + diff);
  x.setHours(0, 0, 0, 0);
  return x;
}
const isoDate = (d) => d.toISOString().slice(0, 10);

export default function DashboardPage() {
  const { t } = useTranslation("app");
  const { user } = useAuth();
  const isViewer = user?.role === "viewer";
  const isAdmin = user?.role === "admin";
  const isCapoCantiere = user?.role === "capo_cantiere" || user?.role === "capo_officina";
  // FIX iter 30h: leggi query params per pre-applicare un filtro data
  // (es. arrivando da WeekView su un giorno con 2+ rapportini).
  const _urlParams = typeof window !== "undefined"
    ? new URLSearchParams(window.location.search)
    : null;
  const _initialDateFrom = _urlParams?.get("date_from") || "";
  const _initialDateTo = _urlParams?.get("date_to") || "";
  const _initialMode = _initialDateFrom || _initialDateTo ? "custom" : "week";
  const [rapportini, setRapportini] = useState([]);
  const [commesse, setCommesse] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState("");
  // Iter 31ll: ordinamento lista (data/numero/commessa/cliente, asc/desc)
  const [sortBy, setSortBy] = useState("data_desc");
  // Iter 30t: deep-link da Clienti/Commesse — `?commessa=EG-XX` prefilla il filtro
  const _initialFilterCommessa = (() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const c = sp.get("commessa");
      return c && c.trim() ? c.trim() : "__all__";
    } catch (_) {
      return "__all__";
    }
  })();
  const [filterCommessa, setFilterCommessa] = useState(_initialFilterCommessa);
  const [filterEmployee, setFilterEmployee] = useState("__all__");
  const [mineOnly, setMineOnly] = useState(false);
  // Mode: "week" (navigation by week) or "custom" (date_from + date_to)
  const [mode, setMode] = useState(_initialMode);
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [customFrom, setCustomFrom] = useState(_initialDateFrom);
  const [customTo, setCustomTo] = useState(_initialDateTo);
  const [loading, setLoading] = useState(true);
  const [toDelete, setToDelete] = useState(null);

  // Vidimazione state (solo admin):
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [unvidCount, setUnvidCount] = useState(0);
  const [vidimaBusy, setVidimaBusy] = useState(false);

  const weekEnd = useMemo(() => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 6);
    return d;
  }, [weekStart]);

  // Effective date range used for query + display
  const effectiveRange = useMemo(() => {
    if (mode === "all") {
      // Iter 31ll: "Tutti i rapportini" — nessun filtro data
      return { from: null, to: null };
    }
    if (mode === "custom") {
      return { from: customFrom || null, to: customTo || null };
    }
    return { from: isoDate(weekStart), to: isoDate(weekEnd) };
  }, [mode, customFrom, customTo, weekStart, weekEnd]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (effectiveRange.from) params.date_from = effectiveRange.from;
      if (effectiveRange.to) params.date_to = effectiveRange.to;
      if (filterCommessa && filterCommessa !== "__all__") params.commessa_codice = filterCommessa;
      if (filterEmployee && filterEmployee !== "__all__") params.employee_id = filterEmployee;
      if (mineOnly) params.mine_only = true;
      const [rapsRes, commRes, empRes] = await Promise.all([
        api.get("/rapportini", { params }),
        api.get("/commesse"),
        api.get("/employees"),
      ]);
      setRapportini(rapsRes.data || []);
      setCommesse(commRes.data || []);
      setEmployees(empRes.data || []);
    } catch (e) {
      toast.error("Impossibile caricare i dati");
    } finally {
      setLoading(false);
    }
  };

  // Iter 31ll: anagrafica clienti per l'assegnazione inline (solo admin)
  useEffect(() => {
    if (!isAdmin) return;
    api
      .get("/clienti")
      .then(({ data }) => setClienti(data || []))
      .catch((err) => console.warn("[lista] caricamento clienti fallito:", err?.message));
    // eslint-disable-next-line
  }, [isAdmin]);

  // Iter 43: bozze in sospeso (proprie; per gli admin: di tutti)
  const [bozze, setBozze] = useState([]);
  const loadBozze = () => {
    if (isViewer) return;
    api
      .get("/bozze")
      .then(({ data }) => setBozze(data || []))
      .catch((err) => console.warn("[lista] caricamento bozze fallito:", err?.message));
  };
  useEffect(() => {
    loadBozze();
    // eslint-disable-next-line
  }, []);

  const deleteBozza = async (b) => {
    const who = b.user_id === user?.id ? "la tua bozza" : `la bozza di ${b.user_name}`;
    if (!window.confirm(`Eliminare ${who}? L'operazione non è reversibile.`)) return;
    try {
      await api.delete(`/bozze/${b.id}`);
      if (b.user_id === user?.id) {
        try { localStorage.removeItem("rapportino_draft_v1"); } catch {}
      }
      toast.success("Bozza eliminata");
      loadBozze();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Eliminazione non riuscita";
      toast.error(typeof msg === "string" ? msg : "Eliminazione non riuscita");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [weekStart, filterCommessa, filterEmployee, mode, customFrom, customTo, mineOnly]);

  // Carica conteggio rapportini non vidimati (per banner admin)
  const loadUnvidCount = async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/rapportini/unvidimati-count");
      setUnvidCount(data?.count || 0);
    } catch {
      setUnvidCount(0);
    }
  };
  useEffect(() => {
    loadUnvidCount();
    // eslint-disable-next-line
  }, [rapportini]);

  // ------------------------------------------------------------------------
  // Iter 30p: Alert "commesse senza ore" per admin al primo login della sessione
  // ------------------------------------------------------------------------
  const [emptyCommesse, setEmptyCommesse] = useState([]);
  const [emptyAlertOpen, setEmptyAlertOpen] = useState(false);
  const [selectedEmptyIds, setSelectedEmptyIds] = useState(new Set());
  const [emptyBusy, setEmptyBusy] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    // Mostra l'alert UNA VOLTA per sessione
    const flag = "empty_commesse_alert_shown_v1";
    if (sessionStorage.getItem(flag)) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/empty-commesse");
        if (cancelled) return;
        const arr = Array.isArray(data) ? data : [];
        if (arr.length > 0) {
          setEmptyCommesse(arr);
          setSelectedEmptyIds(new Set(arr.map((c) => c.id)));
          setEmptyAlertOpen(true);
        }
        sessionStorage.setItem(flag, "1");
      } catch (err) {
        // Non disturbiamo l'utente se l'endpoint fallisce, ma logghiamo
        console.warn("[dashboard] check commesse vuote fallito:", err?.message);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [isAdmin]);

  const toggleEmptySelect = (id) => {
    setSelectedEmptyIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const trashEmptyCommesse = async () => {
    if (selectedEmptyIds.size === 0) {
      setEmptyAlertOpen(false);
      return;
    }
    setEmptyBusy(true);
    try {
      const { data } = await api.post("/admin/empty-commesse/bulk-trash", {
        ids: Array.from(selectedEmptyIds),
      });
      toast.success(
        `Spostate nel cestino ${data?.trashed || 0} commesse senza ore.`
      );
      setEmptyAlertOpen(false);
      // Refresh la lista commesse nel dashboard
      load();
    } catch (e) {
      toast.error("Impossibile spostare nel cestino le commesse selezionate");
    } finally {
      setEmptyBusy(false);
    }
  };

  // ------------------------------------------------------------------------
  // Iter 30s: Alert "commesse senza cliente validato" per admin
  // Si attiva DOPO la chiusura dell'alert empty-commesse (oppure se quello
  // non era da mostrare). Mostra dropdown per associare ogni commessa a un
  // cliente con P.IVA registrato in anagrafica.
  // ------------------------------------------------------------------------
  const [noClienteCommesse, setNoClienteCommesse] = useState([]);
  const [clientiValidi, setClientiValidi] = useState([]);
  const [noClienteOpen, setNoClienteOpen] = useState(false);
  const [pendingAssign, setPendingAssign] = useState({}); // {commessaId: clienteName}
  const [noClienteBusy, setNoClienteBusy] = useState(false);

  const checkNoCliente = async () => {
    // Iter 31r: niente più sessionStorage gate. L'utente ha richiesto che
    // l'alert appaia AD OGNI ingresso finché ci sono commesse senza cliente
    // validato. Si chiude solo quando tutte sono assegnate o l'admin clicca
    // "Annulla" (riapparirà al prossimo accesso).
    try {
      const { data } = await api.get("/admin/commesse-no-cliente");
      const cms = data?.commesse || [];
      if (cms.length > 0) {
        setNoClienteCommesse(cms);
        setClientiValidi(data?.clienti_validi || []);
        setNoClienteOpen(true);
      }
    } catch (err) {
      console.warn("[dashboard] check commesse senza cliente fallito:", err?.message);
    }
  };

  // Trigger il check no-cliente DOPO che l'alert empty si è chiuso
  useEffect(() => {
    if (!isAdmin) return;
    // Iter 31r: se l'alert empty è chiuso, parte subito ad ogni mount/login.
    if (!emptyAlertOpen) {
      checkNoCliente();
    }
    // eslint-disable-next-line
  }, [isAdmin, emptyAlertOpen]);

  // Iter 148: assegna subito il cliente alla commessa e alle "sorelle" con lo stesso codice base (es. EG-99-720 e EG-99-720-OFF)
  const assignClienteNow = async (cm, nome) => {
    const cliente = (nome || "").trim();
    if (!cliente) return;
    const base = (cm.codice || "").toUpperCase().replace(/-OFF$/, "");
    const targets = noClienteCommesse.filter(
      (c) => c.id === cm.id || (c.codice || "").toUpperCase().replace(/-OFF$/, "") === base
    );
    let ok = 0;
    for (const t of targets) {
      try {
        await api.put(`/commesse/${t.id}`, { ...t, cliente });
        ok += 1;
      } catch (_) { /* resta nell'elenco */ }
    }
    if (ok > 0) {
      const doneIds = new Set(targets.map((t) => t.id));
      setNoClienteCommesse((prev) => prev.filter((c) => !doneIds.has(c.id)));
      setPendingAssign((prev) => { const n = { ...prev }; doneIds.forEach((i) => delete n[i]); return n; });
      toast.success(ok === 1 ? `Cliente "${cliente}" assegnato a ${cm.codice}` : `Cliente "${cliente}" assegnato a ${ok} commesse (${targets.map((t) => t.codice).join(", ")})`);
      load();
    } else {
      toast.error(`Impossibile assegnare il cliente a ${cm.codice}`);
    }
  };

  const applyNoClienteFixes = async () => {
    const entries = Object.entries(pendingAssign).filter(
      ([_, v]) => v && v.trim()
    );
    if (entries.length === 0) {
      setNoClienteOpen(false);
      return;
    }
    setNoClienteBusy(true);
    let updated = 0;
    let failed = 0;
    for (const [cid, newCliente] of entries) {
      const cm = noClienteCommesse.find((c) => c.id === cid);
      if (!cm) { failed += 1; continue; }
      try {
        await api.put(`/commesse/${cid}`, {
          ...cm,
          cliente: newCliente,
        });
        updated += 1;
      } catch (_) {
        failed += 1;
      }
    }
    if (updated > 0) {
      toast.success(`Aggiornate ${updated} commesse con cliente validato.`);
      load();
    }
    if (failed > 0) {
      toast.error(`Impossibile aggiornare ${failed} commesse.`);
    }
    setNoClienteOpen(false);
    setNoClienteBusy(false);
    setPendingAssign({});
  };

  // --- Vidimazione: helpers admin ---
  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const selectableUnvidIds = useMemo(
    () => rapportini.filter((r) => !r.vidimato).map((r) => r.id),
    [rapportini]
  );

  // Iter 31x: helper per gestire risposta vidima-bulk con righe non risolte
  const _handleVidimaBulkResponse = (data) => {
    if (data?.skipped && data.skipped.length > 0) {
      const lines = data.skipped.slice(0, 3).map((s) => {
        const ore = s.unresolved
          .map((u) => `${u.nome} (${u.ore_tot}h)`)
          .join(", ");
        return `#${s.numero}: ${ore}`;
      });
      const more =
        data.skipped.length > 3 ? `\n…e altri ${data.skipped.length - 3}` : "";
      toast.warning(
        `Vidimati ${data.modified || 0}, ma ${data.skipped.length} bloccati per nomi non riconosciuti. Correggi i nomi nei rapportini:\n${lines.join("\n")}${more}`,
        { duration: 12000 }
      );
    } else {
      toast.success(
        `Vidimati ${data?.modified || 0} rapportini${data?.resolved_total ? ` (${data.resolved_total} nomi auto-corretti)` : ""}`
      );
    }
  };

  const vidimaSelezionati = async () => {
    if (selectedIds.size === 0) {
      toast.info("Seleziona almeno un rapportino non vidimato");
      return;
    }
    setVidimaBusy(true);
    try {
      const { data } = await api.post("/rapportini/vidima-bulk", {
        ids: Array.from(selectedIds),
      });
      _handleVidimaBulkResponse(data);
      clearSelection();
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Vidimazione non riuscita";
      toast.error(typeof msg === "string" ? msg : "Vidimazione non riuscita");
    } finally {
      setVidimaBusy(false);
    }
  };

  const vidimaTutti = async () => {
    if (!window.confirm(
      `Vidimare TUTTI i ${unvidCount} rapportini non vidimati?\n` +
      "Verranno inclusi nei totali ufficiali."
    )) return;
    setVidimaBusy(true);
    try {
      const { data } = await api.post("/rapportini/vidima-bulk", { all: true });
      _handleVidimaBulkResponse(data);
      clearSelection();
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Vidimazione non riuscita";
      toast.error(typeof msg === "string" ? msg : "Vidimazione non riuscita");
    } finally {
      setVidimaBusy(false);
    }
  };

  const vidimaSingolo = async (id) => {
    try {
      const { data } = await api.post(`/rapportini/${id}/vidima`);
      if (data?.resolved > 0) {
        toast.success(
          `Vidimato (${data.resolved} ${data.resolved === 1 ? "nome auto-corretto" : "nomi auto-corretti"})`
        );
      } else {
        toast.success("Rapportino vidimato");
      }
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 422 && detail?.unresolved) {
        const lines = detail.unresolved
          .map((u) => `• ${u.nome} (${u.ore_tot}h, ${u.giorno})`)
          .join("\n");
        toast.error(
          `Vidimazione bloccata. Le ore di questi nomi andrebbero perse:\n${lines}\n\nApri il rapportino e correggi i nomi.`,
          { duration: 15000 }
        );
      } else {
        toast.error(
          typeof detail === "string" ? detail : "Vidimazione non riuscita"
        );
      }
    }
  };

  const annullaVidima = async (id) => {
    if (!window.confirm("Annullare la vidimazione di questo rapportino?\nSarà escluso dai totali ufficiali fino a nuova vidimazione."))
      return;
    try {
      await api.post(`/rapportini/${id}/annulla-vidima`);
      toast.success("Vidimazione annullata");
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Operazione non riuscita";
      toast.error(typeof msg === "string" ? msg : "Operazione non riuscita");
    }
  };

  // Helper to flatten all righe (multi-day aware)
  const allRighe = (r) => {
    const giorni = r.giorni && r.giorni.length ? r.giorni : null;
    if (giorni) return giorni.flatMap((g) => g.righe || []);
    return r.righe || [];
  };
  const allDayFields = (r) => {
    const giorni = r.giorni && r.giorni.length ? r.giorni : [r];
    return giorni;
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return rapportini;
    const q = search.toLowerCase();
    return rapportini.filter((r) => {
      const numStr = String(r.numero || "");
      if (numStr.includes(q)) return true;
      const days = allDayFields(r);
      const inHeader = days.some((d) =>
        (d.annotazioni || "").toLowerCase().includes(q) ||
        (d.pranzo || "").toLowerCase().includes(q) ||
        (d.pernotto || "").toLowerCase().includes(q) ||
        (d.mezzi_guidatori || "").toLowerCase().includes(q)
      );
      const inRighe = allRighe(r).some(
        (rg) =>
          (rg.commessa_codice || "").toLowerCase().includes(q) ||
          (rg.employee_nome || "").toLowerCase().includes(q) ||
          (rg.note || "").toLowerCase().includes(q)
      );
      return inHeader || inRighe;
    });
    // eslint-disable-next-line
  }, [rapportini, search]);

  // Iter 31ll: mappa codice→commessa (per cliente inline e ordinamenti)
  const commessaByCode = useMemo(() => {
    const m = {};
    for (const c of commesse) m[(c.codice || "").toUpperCase()] = c;
    return m;
  }, [commesse]);

  // Chiave di ordinamento commessa: prefisso, poi codice cliente, poi numero
  // (es. EG-XX-XXX); i codici fuori schema vanno in fondo (alfabetico).
  const commessaSortKey = (code) => {
    const m = /^[A-Z]+-(\d{1,4})-(\d{1,5})/i.exec(code || "");
    if (m) return [0, parseInt(m[1], 10), parseInt(m[2], 10), code || ""];
    return [1, 0, 0, code || ""];
  };
  const _cmpKeys = (ka, kb) => {
    for (let i = 0; i < ka.length; i++) {
      if (ka[i] < kb[i]) return -1;
      if (ka[i] > kb[i]) return 1;
    }
    return 0;
  };

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const firstCode = (r) => {
      const codes = allRighe(r).map((rg) => rg.commessa_codice).filter(Boolean);
      if (codes.length === 0) return "";
      return codes.sort((a, b) => _cmpKeys(commessaSortKey(a), commessaSortKey(b)))[0];
    };
    const clienteOf = (r) => {
      const code = firstCode(r);
      return (commessaByCode[(code || "").toUpperCase()]?.cliente || "").toLowerCase();
    };
    const dataOf = (r) => r.data_inizio || r.data || "";
    const cmp = {
      data_desc: (a, b) => dataOf(b).localeCompare(dataOf(a)),
      data_asc: (a, b) => dataOf(a).localeCompare(dataOf(b)),
      numero_desc: (a, b) => (b.numero || 0) - (a.numero || 0),
      numero_asc: (a, b) => (a.numero || 0) - (b.numero || 0),
      commessa_asc: (a, b) =>
        _cmpKeys(commessaSortKey(firstCode(a)), commessaSortKey(firstCode(b))),
      commessa_desc: (a, b) =>
        _cmpKeys(commessaSortKey(firstCode(b)), commessaSortKey(firstCode(a))),
      cliente_asc: (a, b) => {
        const ca = clienteOf(a), cb = clienteOf(b);
        if (!ca && cb) return 1; // senza cliente in fondo
        if (ca && !cb) return -1;
        return ca.localeCompare(cb);
      },
      cliente_desc: (a, b) => {
        const ca = clienteOf(a), cb = clienteOf(b);
        if (!ca && cb) return 1;
        if (ca && !cb) return -1;
        return cb.localeCompare(ca);
      },
    }[sortBy];
    if (cmp) arr.sort(cmp);
    return arr;
    // eslint-disable-next-line
  }, [filtered, sortBy, commessaByCode]);

  const totaleOre = useMemo(
    () =>
      filtered.reduce(
        (sum, r) =>
          sum + allRighe(r).reduce((s, rg) => s + Number(rg.ore_tot || 0), 0),
        0
      ),
    // eslint-disable-next-line
    [filtered]
  );

  // Ore totali SOLO dei rapportini vidimati (= conteggiate nei report ufficiali)
  const oreVidimate = useMemo(
    () =>
      filtered.reduce(
        (sum, r) =>
          r.vidimato
            ? sum + allRighe(r).reduce((s, rg) => s + Number(rg.ore_tot || 0), 0)
            : sum,
        0
      ),
    // eslint-disable-next-line
    [filtered]
  );

  const countUnvidVisible = useMemo(
    () => filtered.filter((r) => !r.vidimato).length,
    [filtered]
  );

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/rapportini/${toDelete.id}`);
      toast.success("Rapportino eliminato");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error("Eliminazione non riuscita");
    }
  };

  const resetFilters = () => {
    setSearch("");
    setFilterCommessa("__all__");
    setFilterEmployee("__all__");
    setMode("week");
    setWeekStart(startOfWeek(new Date()));
    setCustomFrom("");
    setCustomTo("");
  };

  // CSV export of filtered results (one row per riga, multi-day flattened)
  const exportCsv = () => {
    if (!filtered.length) {
      toast.info("Nessun dato da esportare");
      return;
    }
    const rows = [[
      "Rapportino", "Data", "Commessa", "Dipendente",
      "Ore guida", "Ore passegg.", "Ore lavoro", "Ore caldo", "Ore notturne", "Ore tot.", "Note",
    ]];
    for (const r of filtered) {
      const giorni = r.giorni && r.giorni.length ? r.giorni : [{ data: r.data, righe: r.righe || [] }];
      for (const g of giorni) {
        for (const rg of (g.righe || [])) {
          rows.push([
            `#${r.numero}`,
            g.data || "",
            rg.commessa_codice || "",
            rg.employee_nome || "",
            Number(rg.ore_guida || 0).toFixed(1),
            Number(rg.ore_passegg || 0).toFixed(1),
            Number(rg.ore_lavoro || 0).toFixed(1),
            Number(rg.ore_caldo || 0).toFixed(1),
            Number(rg.ore_notturne || 0).toFixed(1),
            Number(rg.ore_tot || 0).toFixed(1),
            (rg.note || "").replace(/[\r\n]+/g, " "),
          ]);
        }
      }
    }
    const csv = rows
      .map((row) => row.map((v) => {
        const s = String(v ?? "");
        return s.includes(";") || s.includes('"') || s.includes("\n")
          ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(";"))
      .join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const tag = mode === "all"
      ? "tutti"
      : mode === "custom"
      ? `${customFrom || "tutto"}_${customTo || "oggi"}`
      : `${isoDate(weekStart)}_${isoDate(weekEnd)}`;
    a.download = `rapportini_${tag}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const rangeLabel = () => {
    if (mode === "all") return "Tutti i rapportini";
    if (mode === "custom") {
      if (!customFrom && !customTo) return "Tutti i rapportini";
      if (customFrom && customTo) return `Dal ${customFrom} al ${customTo}`;
      if (customFrom) return `Dal ${customFrom}`;
      return `Fino al ${customTo}`;
    }
    return `Settimana ${weekStart.toLocaleDateString("it-IT")} → ${weekEnd.toLocaleDateString("it-IT")}`;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
            {t("list.kicker")}
          </div>
          <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight">
            {t("list.title")}
          </h1>
          <p className="text-sm text-slate-500 mt-1" data-testid="range-label">
            {rangeLabel()}
          </p>
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            className="h-12"
            onClick={exportCsv}
            data-testid="export-csv-button"
          >
            <Download className="h-4 w-4 md:mr-2" />
            <span className="hidden md:inline">{t("list.exportCsv")}</span>
          </Button>
          {!isViewer && (
            <Link to="/rapportino/new">
              <Button
                data-testid="new-rapportino-button"
                className="h-12 px-5 text-base font-bold bg-[#0044FF] hover:bg-[#0033CC] w-full md:w-auto"
              >
                <Plus className="h-5 w-5 mr-2" /> {t("list.nuovo")}
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Iter 43: bozze in sospeso (autore + admin) */}
      {bozze.length > 0 && (
        <Card
          data-testid="bozze-banner"
          className="p-4 md:p-5 border-2 border-sky-300 bg-sky-50"
        >
          <div className="flex items-center gap-2 font-heading font-bold text-sky-900">
            <NotebookPen className="h-5 w-5" />
            {t("list.bozzeTitle", { count: bozze.length })}
          </div>
          <div className="mt-3 space-y-2">
            {bozze.map((b) => {
              const mine = b.user_id === user?.id;
              const s = b.summary || {};
              return (
                <div
                  key={b.id}
                  data-testid={`bozza-row-${b.id}`}
                  className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3 bg-white rounded-lg border border-sky-200 p-3"
                >
                  <div className="flex-1 min-w-0 text-sm">
                    <div className="font-bold text-sky-950">
                      {mine ? t("list.tuaBozza") : `${t("list.bozzaDi")} ${b.user_name}`}
                      {s.numero ? (
                        <span className="font-mono font-normal text-slate-600">
                          {" "}· rapportino n. {s.numero}
                        </span>
                      ) : null}
                    </div>
                    <div className="text-xs text-slate-600 mt-0.5">
                      {s.data_inizio && (
                        <>dal {new Date(s.data_inizio).toLocaleDateString("it-IT")} </>
                      )}
                      {s.n_giorni > 0 && <>· {s.n_giorni} {s.n_giorni === 1 ? "giorno" : "giorni"} </>}
                      {s.n_righe > 0 && <>· {s.n_righe} righe </>}
                      {(s.commesse || []).length > 0 && (
                        <>· commesse: <span className="font-mono">{s.commesse.join(", ")}</span></>
                      )}
                      {b.updated_at && (
                        <>
                          {" "}· aggiornata il{" "}
                          {new Date(b.updated_at).toLocaleString("it-IT", {
                            day: "2-digit", month: "2-digit",
                            hour: "2-digit", minute: "2-digit",
                          })}
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {mine && (
                      <Link to="/rapportino/new?bozza=server">
                        <Button
                          size="sm"
                          className="h-9 bg-sky-600 hover:bg-sky-700 text-white"
                          data-testid={`bozza-riprendi-${b.id}`}
                        >
                          <Pencil className="h-4 w-4 mr-1.5" /> {t("list.riprendi")}
                        </Button>
                      </Link>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-9 text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => deleteBozza(b)}
                      data-testid={`bozza-elimina-${b.id}`}
                    >
                      <Trash2 className="h-4 w-4 mr-1.5" /> {t("list.elimina")}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Alert admin: rapportini non vidimati nel cloud */}
      {isAdmin && unvidCount > 0 && (
        <Card
          data-testid="unvidimati-alert-banner"
          className="p-4 md:p-5 border-2 border-amber-300 bg-amber-50"
        >
          <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
            <div className="flex items-start gap-3 flex-1">
              <AlertTriangle className="h-6 w-6 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <div className="font-heading font-bold text-amber-900">
                  {t("list.unvidTitle", { count: unvidCount })}
                </div>
                <div className="text-sm text-amber-800 mt-0.5">
                  {t("list.unvidSub")}
                </div>
              </div>
            </div>
            <Button
              onClick={vidimaTutti}
              disabled={vidimaBusy}
              data-testid="vidima-tutti-button"
              className="h-11 bg-amber-600 hover:bg-amber-700 text-white whitespace-nowrap shrink-0"
            >
              <CheckCircle2 className="h-4 w-4 mr-2" />
              {t("list.vidimaTutti")}
            </Button>
          </div>
        </Card>
      )}

      {/* Toolbar selezione vidimazione (admin only, mostrata se almeno un selezionato) */}
      {isAdmin && selectedIds.size > 0 && (
        <Card
          data-testid="vidima-selection-toolbar"
          className="p-3 md:p-4 border-2 border-[#0044FF] bg-blue-50 sticky top-16 z-20"
        >
          <div className="flex items-center gap-3 flex-wrap">
            <CheckCircle2 className="h-5 w-5 text-[#0044FF]" />
            <div className="font-bold text-[#0044FF] flex-1">
              {t("list.selCount", { count: selectedIds.size })}
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-10"
              onClick={clearSelection}
              data-testid="vidima-clear-selection"
            >
              <X className="h-4 w-4 mr-1" /> Annulla selezione
            </Button>
            <Button
              size="sm"
              onClick={vidimaSelezionati}
              disabled={vidimaBusy}
              data-testid="vidima-selezionati-button"
              className="h-10 bg-[#0044FF] hover:bg-[#0033CC]"
            >
              <CheckCircle2 className="h-4 w-4 mr-2" /> {t("list.vidimaSel")}
            </Button>
          </div>
        </Card>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          variant={mode === "week" ? "default" : "outline"}
          size="sm"
          className={mode === "week" ? "bg-[#0044FF] hover:bg-[#0033CC] h-10" : "h-10"}
          onClick={() => setMode("week")}
          data-testid="mode-week-button"
        >
          <Calendar className="h-4 w-4 mr-2" /> {t("list.perSettimana")}
        </Button>
        <Button
          variant={mode === "custom" ? "default" : "outline"}
          size="sm"
          className={mode === "custom" ? "bg-[#0044FF] hover:bg-[#0033CC] h-10" : "h-10"}
          onClick={() => setMode("custom")}
          data-testid="mode-custom-button"
        >
          <Filter className="h-4 w-4 mr-2" /> {t("list.intervallo")}
        </Button>
        <Button
          variant={mode === "all" ? "default" : "outline"}
          size="sm"
          className={mode === "all" ? "bg-[#0044FF] hover:bg-[#0033CC] h-10" : "h-10"}
          onClick={() => setMode("all")}
          data-testid="mode-all-button"
        >
          <ClipboardList className="h-4 w-4 mr-2" /> {t("list.tuttiRapp")}
        </Button>
        {/* Iter 31ll: ordinamento lista */}
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="h-10 w-[210px]" data-testid="sort-select">
            <SelectValue placeholder={t("list.ordina")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="data_desc">{t("list.sort.dataDesc")}</SelectItem>
            <SelectItem value="data_asc">{t("list.sort.dataAsc")}</SelectItem>
            <SelectItem value="numero_desc">{t("list.sort.numDesc")}</SelectItem>
            <SelectItem value="numero_asc">{t("list.sort.numAsc")}</SelectItem>
            <SelectItem value="commessa_asc">{t("list.sort.cmAsc")}</SelectItem>
            <SelectItem value="commessa_desc">{t("list.sort.cmDesc")}</SelectItem>
            <SelectItem value="cliente_asc">{t("list.sort.clAsc")}</SelectItem>
            <SelectItem value="cliente_desc">{t("list.sort.clDesc")}</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant="ghost"
          size="sm"
          onClick={resetFilters}
          className="h-10 ml-auto"
          data-testid="reset-filters-button"
        >
          <X className="h-4 w-4 mr-1" /> {t("list.reset")}
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-4 md:p-5">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-3">
            <label className="text-xs font-bold uppercase tracking-wide text-slate-600">
              {t("list.cerca")}
            </label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                data-testid="search-input"
                placeholder={t("list.searchPh")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-12 pl-10"
              />
            </div>
          </div>

          <div className="md:col-span-3">
            <label className="text-xs font-bold uppercase tracking-wide text-slate-600">
              {t("list.dataPrecisa")}
            </label>
            <div className="relative mt-1">
              <Input
                type="date"
                data-testid="filter-exact-date-input"
                value={mode === "custom" && customFrom && customFrom === customTo ? customFrom : ""}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v) {
                    setMode("custom");
                    setCustomFrom(v);
                    setCustomTo(v);
                  } else {
                    setMode("week");
                    setCustomFrom("");
                    setCustomTo("");
                  }
                }}
                className="h-12 w-full min-w-0 pr-9"
              />
              {mode === "custom" && customFrom && customFrom === customTo && (
                <button
                  type="button"
                  onClick={() => {
                    setMode("week");
                    setCustomFrom("");
                    setCustomTo("");
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="filter-exact-date-clear"
                  title={t("list.reset")}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          <div className="md:col-span-3">
            <label className="text-xs font-bold uppercase tracking-wide text-slate-600">
              {t("rapp.commessa")}
            </label>
            <Select value={filterCommessa} onValueChange={setFilterCommessa}>
              <SelectTrigger
                data-testid="filter-commessa-select"
                className="h-12 mt-1"
              >
                <SelectValue placeholder={t("list.tutte")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{t("riep.tutte")}</SelectItem>
                {commesse.map((c) => (
                  <SelectItem key={c.id} value={c.codice}>
                    {c.codice} {c.descrizione ? `· ${c.descrizione}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="md:col-span-3">
            <label className="text-xs font-bold uppercase tracking-wide text-slate-600">
              {t("list.dipendente")}
            </label>
            <Select value={filterEmployee} onValueChange={setFilterEmployee}>
              <SelectTrigger
                data-testid="filter-employee-select"
                className="h-12 mt-1"
              >
                <SelectValue placeholder={t("list.tutti")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{t("list.tuttiDip")}</SelectItem>
                {employees.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.cognome} {e.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Toggle "Solo i miei rapportini" — visibile solo a chi vede tutto */}
          {user?.can_view_all_rapportini && (
            <div className="md:col-span-12">
              <label
                className="flex items-center gap-3 bg-slate-50 border rounded-md px-3 py-2 cursor-pointer hover:bg-slate-100 transition"
                data-testid="mine-only-toggle-label"
              >
                <input
                  type="checkbox"
                  checked={mineOnly}
                  onChange={(e) => setMineOnly(e.target.checked)}
                  className="h-4 w-4 accent-[#0044FF]"
                  data-testid="mine-only-toggle"
                />
                <span className="text-sm">
                  <b>{t("list.soloMiei")}</b>
                  <span className="text-slate-500 ml-2">
                    {t("list.soloMieiSub")}
                  </span>
                </span>
              </label>
            </div>
          )}

          {/* Date controls: week navigation OR custom range (niente in "tutti") */}
          {mode === "all" ? null : mode === "week" ? (
            <div className="md:col-span-12">
              <label className="text-xs font-bold uppercase tracking-wide text-slate-600">
                {t("list.settimana")}
              </label>
              <div className="flex items-center gap-2 mt-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-12 w-12"
                  data-testid="prev-week-button"
                  onClick={() => {
                    const d = new Date(weekStart);
                    d.setDate(d.getDate() - 7);
                    setWeekStart(d);
                  }}
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <Button
                  variant="outline"
                  className="h-12 flex-1"
                  data-testid="current-week-button"
                  onClick={() => setWeekStart(startOfWeek(new Date()))}
                >
                  <Calendar className="h-4 w-4 mr-2" /> {t("week.thisWeek")}
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-12 w-12"
                  data-testid="next-week-button"
                  onClick={() => {
                    const d = new Date(weekStart);
                    d.setDate(d.getDate() + 7);
                    setWeekStart(d);
                  }}
                >
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="md:col-span-6">
                <label className="text-xs font-bold uppercase tracking-wide text-slate-600">
                  {t("list.da")}
                </label>
                <Input
                  data-testid="filter-date-from"
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="h-12 mt-1"
                />
              </div>
              <div className="md:col-span-6">
                <label className="text-xs font-bold uppercase tracking-wide text-slate-600">
                  {t("list.a")}
                </label>
                <Input
                  data-testid="filter-date-to"
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="h-12 mt-1"
                />
              </div>
            </>
          )}
        </div>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label={t("list.statRapp")} value={filtered.length} sub={countUnvidVisible > 0 ? `${countUnvidVisible} ${t("list.daVidimare")}` : null} />
        <Stat
          label={t("list.statOre")}
          value={totaleOre.toFixed(1)}
          sub={
            countUnvidVisible > 0
              ? `${oreVidimate.toFixed(1)} ${t("list.vidimate")}, ${(totaleOre - oreVidimate).toFixed(1)} ${t("list.daApprovare")}`
              : null
          }
          testid="stat-ore-totali"
        />
        <Stat
          label={t("list.statCommesse")}
          value={
            new Set(
              filtered.flatMap((r) =>
                allRighe(r).map((rg) => rg.commessa_codice).filter(Boolean)
              )
            ).size
          }
        />
        <Stat
          label={t("list.statDip")}
          value={
            new Set(
              filtered.flatMap((r) =>
                allRighe(r).map((rg) => rg.employee_nome).filter(Boolean)
              )
            ).size
          }
        />
      </div>

      {/* Mini-grafico ore per dipendente */}
      <HoursByEmployeeChart
        dateFrom={effectiveRange.from}
        dateTo={effectiveRange.to}
        onSelectEmployee={(eid) =>
          setFilterEmployee(filterEmployee === eid ? "__all__" : eid)
        }
        selectedEmployee={filterEmployee !== "__all__" ? filterEmployee : null}
      />

      {/* List */}
      <div className="space-y-3">
        {loading ? (
          <div className="text-center text-slate-500 py-10">{t("week.loading")}</div>
        ) : filtered.length === 0 ? (
          <Card className="p-10 text-center" data-testid="empty-state">
            <ClipboardList className="h-10 w-10 mx-auto text-slate-300" />
            <div className="font-heading font-bold text-lg mt-3">
              {t("list.emptyTitle")}
            </div>
            <p className="text-sm text-slate-500 mt-1">
              {t("list.emptySub")}
            </p>
            {!isViewer && (
              <Link to="/rapportino/new" className="inline-block mt-4">
                <Button className="h-12 bg-[#0044FF] hover:bg-[#0033CC]">
                  <Plus className="h-5 w-5 mr-2" /> {t("list.nuovoRapp")}
                </Button>
              </Link>
            )}
          </Card>
        ) : (
          sorted.map((r) => {
            const righe = allRighe(r);
            const ore = righe.reduce((s, rg) => s + Number(rg.ore_tot || 0), 0);
            const commesseSet = Array.from(
              new Set(righe.map((rg) => rg.commessa_codice).filter(Boolean))
            ).sort((a, b) => _cmpKeys(commessaSortKey(a), commessaSortKey(b)));
            const di = r.data_inizio || r.data;
            const df = r.data_fine || r.data;
            const dateLabel =
              di === df
                ? formatDateIT(di)
                : `${formatDateIT(di)} → ${formatDateIT(df)}`;
            const numGiorni = (r.giorni && r.giorni.length) || 1;
            const isVidimato = !!r.vidimato;
            const isSelected = selectedIds.has(r.id);
            return (
              <Card
                key={r.id}
                data-testid={`rapportino-card-${r.id}`}
                className={`p-4 md:p-5 transition-colors ${
                  isSelected
                    ? "border-2 border-[#0044FF] bg-blue-50/50"
                    : !isVidimato
                    ? "border-l-4 border-l-amber-400 hover:border-[#0044FF]"
                    : "hover:border-[#0044FF]"
                }`}
              >
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    {isAdmin && !isVidimato && (
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(r.id)}
                        data-testid={`vidima-checkbox-${r.id}`}
                        title="Seleziona per vidimazione massiva"
                        className="h-5 w-5 shrink-0 accent-[#0044FF] cursor-pointer"
                      />
                    )}
                    <div className="h-14 min-w-[3.5rem] px-2 shrink-0 rounded-md bg-slate-900 text-white grid place-items-center" data-testid={`rapp-numero-badge-${r.id}`}>
                      <div className="text-center">
                        <div className="text-[9px] uppercase tracking-wider text-slate-400 leading-none">n°</div>
                        <div className={`font-heading font-black tabular-nums leading-tight ${String(r.numero).length > 5 ? "text-sm" : String(r.numero).length > 4 ? "text-base" : "text-lg"}`}>
                          {r.numero}
                        </div>
                        <div className="text-[9px] uppercase tracking-wider text-slate-300 leading-none">
                          rapp.
                        </div>
                      </div>
                    </div>
                    <div className="min-w-0">
                      <div className="font-heading font-bold text-base capitalize truncate flex items-center flex-wrap gap-2">
                        <span>{dateLabel}</span>
                        {numGiorni > 1 && (
                          <span className="text-xs font-normal text-[#0044FF] uppercase tracking-wide">
                            · {numGiorni} {t("list.giorni")}
                          </span>
                        )}
                        {isVidimato ? (
                          <span
                            data-testid={`vidimato-badge-${r.id}`}
                            title={
                              r.vidimato_da_nome
                                ? `Vidimato da ${r.vidimato_da_nome}`
                                : "Vidimato"
                            }
                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-800"
                          >
                            <CheckCircle2 className="h-3 w-3" />
                            {t("list.vidimato")}
                          </span>
                        ) : (
                          <span
                            data-testid={`unvidimato-badge-${r.id}`}
                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-amber-100 text-amber-800"
                          >
                            <Clock className="h-3 w-3" />
                            {t("list.daVidimareBadge")}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-slate-600 flex items-center flex-wrap gap-x-3 gap-y-0.5">
                        {commesseSet.length > 0 ? (
                          commesseSet.map((code) => (
                            <CommessaClienteChip
                              key={code}
                              codice={code}
                              commessa={commessaByCode[(code || "").toUpperCase()]}
                              clienti={clienti}
                              editable={isAdmin}
                              onSaved={(updated) =>
                                setCommesse((prev) => {
                                  // aggiorna la commessa salvata + le sorelle
                                  // della stessa famiglia (propagate dal backend,
                                  // tranne EG-99 multi-cliente)
                                  const fam = /^EG-(\d{1,2})-/i.exec(updated.codice || "");
                                  const prefix = fam ? `EG-${fam[1]}-`.toUpperCase() : null;
                                  return prev.map((c) => {
                                    if (c.id === updated.id) return updated;
                                    if (
                                      prefix &&
                                      prefix !== "EG-99-" &&
                                      (c.codice || "").toUpperCase().startsWith(prefix)
                                    ) {
                                      return { ...c, cliente: updated.cliente };
                                    }
                                    return c;
                                  });
                                })
                              }
                            />
                          ))
                        ) : (
                          <span>{t("list.nessunaCommessa")}</span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {righe.length} {t("week.rows")} · {ore.toFixed(1)} {t("list.oreTotLower")}
                        {r.created_by_name && (
                          <span className="ml-1">· {t("list.byCreator")} {r.created_by_name}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0 flex-wrap">
                    {isAdmin && !isVidimato && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-11 border-amber-300 text-amber-800 hover:bg-amber-50"
                        data-testid={`vidima-rapportino-${r.id}`}
                        onClick={() => vidimaSingolo(r.id)}
                      >
                        <CheckCircle2 className="h-4 w-4 md:mr-2" />
                        <span className="hidden md:inline">{t("list.vidima")}</span>
                      </Button>
                    )}
                    {isAdmin && isVidimato && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-11 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                        data-testid={`annulla-vidima-${r.id}`}
                        onClick={() => annullaVidima(r.id)}
                        title="Annulla vidimazione"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                    <Link to={`/rapportino/${r.id}/stampa`}>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-11"
                        data-testid={`print-rapportino-${r.id}`}
                      >
                        <Printer className="h-4 w-4 md:mr-2" />
                        <span className="hidden md:inline">{t("rapp.print")}</span>
                      </Button>
                    </Link>
                    <Link to={`/rapportino/${r.id}`}>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-11"
                        data-testid={`edit-rapportino-${r.id}`}
                      >
                        <Pencil className="h-4 w-4 md:mr-2" />
                        <span className="hidden md:inline">
                          {isAdmin ? t("list.modifica") : t("list.apri")}
                        </span>
                      </Button>
                    </Link>
                    {isAdmin && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-11 text-red-600 hover:bg-red-50 hover:text-red-700 border-red-200"
                        data-testid={`delete-rapportino-${r.id}`}
                        onClick={() => setToDelete(r)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })
        )}
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il rapportino?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare il rapportino #{toDelete?.numero}.
              L'operazione non è reversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-rapportino"
              className="bg-red-600 hover:bg-red-700"
              onClick={handleDelete}
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Iter 30p: Alert "commesse senza ore" — mostrato all'admin una volta a sessione */}
      <AlertDialog
        open={emptyAlertOpen}
        onOpenChange={(o) => !o && setEmptyAlertOpen(false)}
      >
        <AlertDialogContent
          className="max-w-2xl"
          data-testid="empty-commesse-alert"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              {emptyCommesse.length} commesse senza ore
            </AlertDialogTitle>
            <AlertDialogDescription>
              Queste commesse non sono mai state usate in nessun rapportino.
              Seleziona quelle che vuoi spostare nel cestino (recuperabili 30
              giorni). Quelle non selezionate restano attive.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="max-h-80 overflow-y-auto border rounded-md divide-y">
            {emptyCommesse.map((c) => {
              const checked = selectedEmptyIds.has(c.id);
              return (
                <label
                  key={c.id}
                  data-testid={`empty-commessa-row-${c.id}`}
                  className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50 ${
                    checked ? "bg-amber-50" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleEmptySelect(c.id)}
                    className="h-4 w-4 accent-amber-600"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-mono font-bold text-sm">{c.codice}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {c.descrizione || "—"}
                      {c.cliente && (
                        <span className="ml-2 text-slate-400">
                          · {c.cliente}
                        </span>
                      )}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>

          <div className="text-xs text-slate-500 flex items-center justify-between mt-2">
            <button
              type="button"
              onClick={() =>
                setSelectedEmptyIds(
                  selectedEmptyIds.size === emptyCommesse.length
                    ? new Set()
                    : new Set(emptyCommesse.map((c) => c.id))
                )
              }
              className="underline hover:text-slate-700"
              data-testid="empty-commesse-toggle-all"
            >
              {selectedEmptyIds.size === emptyCommesse.length
                ? "Deseleziona tutto"
                : "Seleziona tutto"}
            </button>
            <span>{selectedEmptyIds.size} selezionate</span>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="empty-commesse-cancel"
              onClick={() => {
                api.post("/admin/empty-commesse/keep", {
                  ids: emptyCommesse.map((c) => c.id),
                }).catch(() => {});
              }}
            >
              Tienile tutte
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="empty-commesse-trash"
              className="bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
              disabled={emptyBusy || selectedEmptyIds.size === 0}
              onClick={trashEmptyCommesse}
            >
              {emptyBusy
                ? "In corso…"
                : `Sposta nel cestino (${selectedEmptyIds.size})`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Iter 30s: Alert "commesse senza cliente validato" */}
      <AlertDialog
        open={noClienteOpen}
        onOpenChange={(o) => !o && setNoClienteOpen(false)}
      >
        <AlertDialogContent
          className="max-w-2xl"
          data-testid="no-cliente-alert"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              {noClienteCommesse.length} commesse senza cliente validato
            </AlertDialogTitle>
            <AlertDialogDescription>
              Queste commesse hanno un cliente <b>non riconosciuto</b> oppure
              <b> senza P.IVA</b> in anagrafica. Per ognuna scegli un cliente
              esistente OPPURE <b>creane uno nuovo al volo</b> dal combobox
              (digita il nome e premi &quot;Crea nuovo cliente&quot;). La scelta
              viene <b>salvata subito</b>, anche sulla commessa gemella
              (es. -OFF). Le commesse non sistemate restano com&apos;erano e
              l&apos;avviso riapparirà al prossimo accesso.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="max-h-80 overflow-y-auto border rounded-md divide-y">
            {noClienteCommesse.length === 0 && (
              <div className="px-3 py-6 text-sm text-emerald-700 font-bold text-center" data-testid="no-cliente-all-done">
                ✓ Tutte le commesse hanno un cliente validato.
              </div>
            )}
            {clientiValidi.length === 0 && noClienteCommesse.length > 0 && (
              <div className="px-3 py-2 text-xs bg-amber-50 text-amber-900 border-b border-amber-200">
                💡 Nessun cliente con P.IVA in anagrafica. Puoi crearli al volo
                qui sotto: digita il nome nel combobox e clicca <b>&quot;Crea
                nuovo cliente&quot;</b>.
              </div>
            )}
            {noClienteCommesse.map((cm) => (
                <div
                  key={cm.id}
                  data-testid={`no-cliente-row-${cm.id}`}
                  className="px-3 py-2 flex flex-col gap-1.5"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-mono font-bold text-sm">{cm.codice}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {cm.descrizione || "—"}
                        <span className="ml-2 text-amber-700">
                          attuale: {cm.cliente?.trim() ? `"${cm.cliente}"` : "vuoto"}
                        </span>
                      </div>
                    </div>
                  </div>
                  <ClienteCombobox
                    value={pendingAssign[cm.id] || ""}
                    defaultStabilimento={
                      ((cm.codice || "").match(/^EG-(\d{2})/i) || [])[1] || ""
                    }
                    onChange={(nome) => {
                      setPendingAssign((prev) => ({ ...prev, [cm.id]: nome }));
                      // Iter 148: se è un cliente esistente, assegna SUBITO (anche alle sorelle -OFF)
                      if (clientiValidi.some((c) => (c.nome || "").toLowerCase() === (nome || "").trim().toLowerCase())) {
                        assignClienteNow(cm, nome);
                      }
                    }}
                    onClienteCreated={(created) => {
                      // Iter 148: cliente appena creato → assegnato subito a questa commessa e alle sorelle
                      assignClienteNow(cm, created?.nome || "");
                      // Aggiungi all'elenco clientiValidi così appare nelle altre righe
                      setClientiValidi((prev) => {
                        if (prev.find((c) => c.id === created.id)) return prev;
                        return [
                          ...prev,
                          {
                            id: created.id,
                            nome: created.ragione_sociale || created.nome,
                            partita_iva: created.piva || created.partita_iva || "",
                            citta: created.citta || "",
                          },
                        ];
                      });
                    }}
                    clienti={clientiValidi}
                    testId={`no-cliente-combo-${cm.id}`}
                    placeholder="Scegli cliente esistente o crea nuovo…"
                  />
                </div>
              ))}
            </div>

          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <a
              href="/anagrafica/clienti?ai-research=1"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-1.5 h-10 rounded-md px-3 text-sm font-semibold border border-purple-300 bg-purple-50 text-purple-900 hover:bg-purple-100 transition"
              data-testid="no-cliente-open-ai-research"
              title="Apre la schermata Clienti in nuova scheda e avvia automaticamente la ricerca AI dei dati mancanti (P.IVA, indirizzo, città) per i clienti senza P.IVA."
            >
              ✨ Apri Clienti per ricerca AI
            </a>
            <div className="flex gap-2 sm:ml-auto">
              <AlertDialogCancel data-testid="no-cliente-cancel">
                {noClienteCommesse.length === 0 ? "Chiudi" : "Più tardi"}
              </AlertDialogCancel>
              {noClienteCommesse.length > 0 && (
              <AlertDialogAction
                data-testid="no-cliente-save"
                className="bg-[#0044FF] hover:bg-[#0033CC] disabled:opacity-50"
                disabled={
                  noClienteBusy ||
                  Object.values(pendingAssign).filter((v) => v && v.trim()).length === 0
                }
                onClick={applyNoClienteFixes}
              >
                {noClienteBusy
                  ? "In corso…"
                  : `Aggiorna ${
                      Object.values(pendingAssign).filter((v) => v && v.trim()).length
                    } commesse`}
              </AlertDialogAction>
              )}
            </div>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Stat({ label, value, sub, testid }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="text-xs font-bold uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className="font-heading font-black text-3xl tracking-tight mt-1">
        {value}
      </div>
      {sub && (
        <div className="text-[11px] text-amber-700 font-semibold mt-1">{sub}</div>
      )}
    </Card>
  );
}
